import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Breadcrumbs,
  Link,
  Chip,
  Avatar,
  AvatarGroup,
  IconButton,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { 
  Add as AddIcon, 
  PhotoCamera, 
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Business,
  PictureAsPdf,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { DataTable } from '../../components/DataTable/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import { SekurantenDialog } from '../../components/SekurantenDialog/SekurantenDialog';
import { sekurantenProjekteAPI, sekurantenAPI, api } from '../../services/api';
import { SekurantenProjekt, Sekurant } from '../../types/api';
import { useSnackbar } from 'notistack';
import { API_BASE_URL } from '../../config';

const backendUrl = API_BASE_URL;

const columns = [
  { id: 'number', label: 'Nummer' },
  { id: 'description', label: 'Beschreibung' },
  {
    id: 'created_at',
    label: 'Erstellt am',
    format: (value: string) =>
      format(new Date(value), 'dd.MM.yyyy HH:mm', { locale: de }),
  },
  {
    id: 'photos',
    label: 'Fotos',
    format: (photos: string[]) => {
      if (!photos || photos.length === 0) {
        return (
          <Chip 
            icon={<PhotoCamera />} 
            label="Keine Fotos" 
            size="small" 
            variant="outlined" 
          />
        );
      }
      
      if (photos.length === 1) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar 
              variant="rounded" 
              src={`${backendUrl}/${photos[0]}`} 
              alt="Sekurant" 
              sx={{ width: 40, height: 40 }} 
            />
            <Chip 
              icon={<PhotoCamera />} 
              label="1 Foto" 
              size="small" 
              color="primary" 
            />
          </Box>
        );
      }
      
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 32, height: 32 } }}>
            {photos.slice(0, 3).map((photo, index) => (
              <Avatar 
                key={index}
                variant="rounded" 
                src={`${backendUrl}/${photo}`} 
                alt={`Foto ${index + 1}`} 
                sx={{ width: 32, height: 32 }} 
              />
            ))}
          </AvatarGroup>
          <Chip 
            icon={<PhotoCamera />} 
            label={`${photos.length} Fotos`} 
            size="small" 
            color="primary" 
          />
        </Box>
      );
    },
  },
];

export const SekurantenProjektDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [projekt, setProjekt] = useState<SekurantenProjekt | null>(null);
  const [sekuranten, setSekuranten] = useState<Sekurant[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSekurant, setSelectedSekurant] = useState<Sekurant | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSekurant, setEditingSekurant] = useState<Sekurant | null>(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfDeleteDialogOpen, setPdfDeleteDialogOpen] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [viewSekurant, setViewSekurant] = useState<Sekurant | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [zoomedPhoto, setZoomedPhoto] = useState<string | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Rollen-basierte Berechtigungen für Sekuranten
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management';
  const isBauleiter = user?.role === 'bauleiter';
  const isForeman = user?.role === 'foreman';
  const isInstaller = user?.role === 'installer';
  
  // Admin, Management & Bauleiter können alles
  const canSeeAll = isAdmin || isManagement || isBauleiter;
  // Vorarbeiter & Monteure haben eingeschränkte Rechte
  const canSeeRestricted = isForeman || isInstaller;
  
  // Sekuranten-Berechtigungen:
  const canCreateSekuranten = canSeeAll || canSeeRestricted; // Alle können erstellen
  const canEditSekuranten = canSeeAll; // Nur Admin, Management, Bauleiter können alle bearbeiten
  const canDeleteSekuranten = canSeeAll; // Nur Admin, Management, Bauleiter können löschen
  const canUploadPdf = canSeeAll || canSeeRestricted; // Alle können PDF hochladen
  const canDeletePdf = canSeeAll; // Nur Admin, Management, Bauleiter können PDF löschen

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projektData, sekurantenData] = await Promise.all([
        sekurantenProjekteAPI.getProjekt(parseInt(id!)),
        sekurantenAPI.getSekuranten(parseInt(id!))
      ]);
      setProjekt(projektData);
      setSekuranten(sekurantenData);
    } catch (error) {
      console.error('Error fetching data:', error);
      enqueueSnackbar('Fehler beim Laden der Daten', { variant: 'error' });
      navigate('/sekuranten');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingSekurant(null);
    setDialogOpen(true);
  };

  const handleEdit = (sekurant: Sekurant) => {
    setEditingSekurant(sekurant);
    setDialogOpen(true);
  };

  const handleView = (sekurant: Sekurant) => {
    setViewSekurant(sekurant);
    setViewDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedSekurant) return;

    try {
      await sekurantenAPI.deleteSekurant(selectedSekurant.id);
      enqueueSnackbar('Sekurant erfolgreich gelöscht', { variant: 'success' });
      fetchData();
    } catch (error) {
      console.error('Error deleting sekurant:', error);
      enqueueSnackbar('Fehler beim Löschen des Sekuranten', { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedSekurant(null);
    }
  };

  const confirmDelete = (sekurant: Sekurant) => {
    setSelectedSekurant(sekurant);
    setDeleteDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingSekurant(null);
  };

  const handleDialogSave = async () => {
    setDialogOpen(false);
    setEditingSekurant(null);
    await fetchData();
  };

  const handlePdfUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !id) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      enqueueSnackbar('Bitte wählen Sie eine PDF-Datei aus', { variant: 'warning' });
      return;
    }

    try {
      setUploadingPdf(true);
      await sekurantenProjekteAPI.uploadPdfPlan(parseInt(id), file);
      enqueueSnackbar('PDF-Plan erfolgreich hochgeladen', { variant: 'success' });
      await fetchData();
    } catch (error) {
      console.error('Error uploading PDF:', error);
      enqueueSnackbar('Fehler beim Hochladen des PDF-Plans', { variant: 'error' });
    } finally {
      setUploadingPdf(false);
    }
  };

  const handlePdfDelete = async () => {
    if (!id) return;

    try {
      await sekurantenProjekteAPI.deletePdfPlan(parseInt(id));
      enqueueSnackbar('PDF-Plan erfolgreich gelöscht', { variant: 'success' });
      await fetchData();
    } catch (error) {
      console.error('Error deleting PDF:', error);
      enqueueSnackbar('Fehler beim Löschen des PDF-Plans', { variant: 'error' });
    } finally {
      setPdfDeleteDialogOpen(false);
    }
  };

  const handleExportPdf = async () => {
    if (!id) return;
    setExportingPdf(true);
    try {
      const response = await fetch(`${backendUrl}/sekuranten-projekte/${id}/export-pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Fehler beim Generieren der PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sekurantenprojekt_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      enqueueSnackbar('Fehler beim PDF-Export', { variant: 'error' });
    } finally {
      setExportingPdf(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) return;

    const file = event.target.files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/sekuranten-projekte/${id}/upload-excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      enqueueSnackbar('Excel-Datei erfolgreich hochgeladen', { variant: 'success' });
      fetchData();
    } catch (error) {
      console.error('Error uploading file:', error);
      enqueueSnackbar('Fehler beim Hochladen der Excel-Datei', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!projekt) {
    return (
      <Box>
        <Typography variant="h6">Sekuranten-Projekt nicht gefunden</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/sekuranten')}>
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/sekuranten')}
          sx={{ textDecoration: 'none' }}
        >
          Sekuranten-Projekte
        </Link>
        <Typography color="text.primary">{projekt.name}</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h1">
            {projekt.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<GetAppIcon />}
              onClick={handleExportPdf}
              disabled={exportingPdf}
            >
              {exportingPdf ? 'PDF wird erstellt...' : 'Projekt als PDF exportieren'}
            </Button>
          <IconButton onClick={() => navigate(`/sekuranten-projekte/${id}/edit`)}>
            <EditIcon />
          </IconButton>
          </Box>
        </Box>
        
        {projekt.description && (
          <Typography variant="body1" color="text.secondary" paragraph>
            {projekt.description}
          </Typography>
        )}

        {projekt.project_name && (
          <Chip 
            icon={<Business />} 
            label={`Verknüpft mit: ${projekt.project_name}`}
            color="primary"
            variant="outlined"
          />
        )}

        {projekt.objektname && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Objektname:</strong> {projekt.objektname}
          </Typography>
        )}
        {projekt.objektadresse && (
          <Typography variant="body2">
            <strong>Objektadresse:</strong> {projekt.objektadresse}
          </Typography>
        )}
        <Typography variant="body2">
          <strong>Montagefirma:</strong> {projekt.montagefirma_name}
        </Typography>
        <Typography variant="body2">
          <strong>Adresse der Montagefirma:</strong> {projekt.montagefirma_adresse}
        </Typography>
      </Box>

      {/* Unterschrift anzeigen */}
      {projekt.unterschrift_image && (
        <Box sx={{ mb: 3, mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Unterschrift
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <img
              src={`${backendUrl}/${projekt.unterschrift_image}`}
              alt="Unterschrift"
              style={{ maxHeight: 80, maxWidth: 300, border: '1px solid #ccc', background: '#fff', padding: 4, borderRadius: 4 }}
            />
            <Box>
              <Typography variant="body2">
                <strong>Name:</strong> {projekt.unterschrift_name || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Datum:</strong> {projekt.unterschrift_datum ? format(new Date(projekt.unterschrift_datum), 'dd.MM.yyyy HH:mm', { locale: de }) : '-'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* PDF Plan Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            PDF-Plan
          </Typography>
        </Box>
        
        {projekt.pdf_plan_path ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              icon={<PictureAsPdf />}
              label="PDF-Plan vorhanden"
              color="success"
            />
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => setPdfPreviewOpen(true)}
            >
              Anzeigen
            </Button>
            {canDeletePdf && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setPdfDeleteDialogOpen(true)}
              >
                Löschen
              </Button>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Kein PDF-Plan hochgeladen
            </Typography>
            {canUploadPdf && (
              <>
                <input
                  accept=".pdf"
                  style={{ display: 'none' }}
                  id="pdf-upload"
                  type="file"
                  onChange={handlePdfUpload}
                  disabled={uploadingPdf}
                />
                <label htmlFor="pdf-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={uploadingPdf ? <CircularProgress size={20} /> : <UploadIcon />}
                    disabled={uploadingPdf}
                  >
                    {uploadingPdf ? 'Wird hochgeladen...' : 'PDF-Plan hochladen'}
                  </Button>
                </label>
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* Full-width PDF preview below the PDF plan section */}
      {projekt.pdf_plan_path && (
        <Box sx={{ width: '100%', mb: 3 }}>
          <iframe
            src={`${backendUrl}/${projekt.pdf_plan_path}`}
            title="PDF-Plan Vorschau"
            width="100%"
            height="600px"
            style={{ border: '1px solid #ccc', borderRadius: 4 }}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Sekuranten-Dokumente ({sekuranten.length})
        </Typography>
        {canCreateSekuranten && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Neuer Sekurant
          </Button>
        )}
      </Box>

      <Card>
        <CardContent>
          <DataTable
            columns={columns}
            rows={sekuranten}
            loading={false}
            initialSortBy="created_at"
            initialOrder="desc"
            onView={handleView}
            {...(canEditSekuranten && { onEdit: handleEdit })}
            {...(canDeleteSekuranten && { onDelete: confirmDelete })}
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Sekurant löschen"
        message="Sind Sie sicher, dass Sie diesen Sekurant löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden."
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedSekurant(null);
        }}
        severity="error"
      />

      <SekurantenDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        sekurantenProjektId={parseInt(id!)}
        sekurant={editingSekurant}
      />

      {projekt?.pdf_plan_path && (
        <Dialog
          open={pdfPreviewOpen}
          onClose={() => setPdfPreviewOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PictureAsPdf color="error" />
                <Typography>PDF-Plan</Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => window.open(`${backendUrl}/${projekt.pdf_plan_path}`, '_blank')}
              >
                In neuem Tab öffnen
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ height: 600, p: 0 }}>
            <iframe
              src={`${backendUrl}/${projekt.pdf_plan_path}`}
              title="PDF-Plan"
              width="100%"
              height="100%"
              style={{ minHeight: 600, border: 'none' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPdfPreviewOpen(false)}>Schließen</Button>
          </DialogActions>
        </Dialog>
      )}

      <ConfirmDialog
        open={pdfDeleteDialogOpen}
        title="PDF-Plan löschen"
        message="Sind Sie sicher, dass Sie den PDF-Plan löschen möchten?"
        onConfirm={handlePdfDelete}
        onCancel={() => setPdfDeleteDialogOpen(false)}
        severity="warning"
      />

      {viewSekurant && (
        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Sekurant Details</DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle1"><strong>Nummer:</strong> {viewSekurant.number}</Typography>
            <Typography variant="subtitle1"><strong>Beschreibung:</strong> {viewSekurant.description}</Typography>
            <Typography variant="subtitle1"><strong>Name des verantwortlichen Monteurs:</strong> {viewSekurant.verantwortlicher_monteur || '-'}</Typography>
            <Typography variant="subtitle1"><strong>Hersteller der Anschlageinrichtung:</strong> {viewSekurant.hersteller_anschlageinrichtung || '-'}</Typography>
            <Typography variant="subtitle1"><strong>Typ/Modell/Artikelnummer der Anschlageinrichtung:</strong> {viewSekurant.typ_anschlageinrichtung || '-'}</Typography>
            <Typography variant="subtitle1"><strong>Hersteller und Typ der Befestigungsmittel:</strong> {viewSekurant.hersteller_befestigungsmittel || '-'}</Typography>
            <Typography variant="subtitle1"><strong>Erstellt am:</strong> {viewSekurant.created_at ? format(new Date(viewSekurant.created_at), 'dd.MM.yyyy HH:mm', { locale: de }) : '-'}</Typography>
            <Typography variant="subtitle1"><strong>Erstellt von:</strong> {viewSekurant.created_by_name || viewSekurant.created_by || '-'}</Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1"><strong>Fotos:</strong></Typography>
              {viewSekurant.photos && viewSekurant.photos.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                  {viewSekurant.photos.map((photo, idx) => (
                    <Avatar
                      key={idx}
                      variant="rounded"
                      src={`${backendUrl}/${photo}`}
                      alt={`Foto ${idx + 1}`}
                      sx={{ width: 80, height: 80, border: '1px solid #ccc', cursor: 'zoom-in' }}
                      onClick={() => setZoomedPhoto(`${backendUrl}/${photo}`)}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">Keine Fotos vorhanden</Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Schließen</Button>
          </DialogActions>
        </Dialog>
      )}
      {zoomedPhoto && (
        <Dialog open={!!zoomedPhoto} onClose={() => setZoomedPhoto(null)} maxWidth="md" fullWidth>
          <DialogTitle>Foto-Detailansicht</DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
            <img src={zoomedPhoto} alt="Detailansicht" style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8 }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setZoomedPhoto(null)}>Schließen</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}; 